import React, { useEffect, useState } from "react";
import Cookies from "js-cookie";
import {
  Backdrop,
  CircularProgress,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  Box,
  Alert,
  Paper,
  Button,
  Divider,
} from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import DownloadIcon from "@mui/icons-material/Download";
import Formulario from "../../utils/forms/Formulario";
const admin_service = require("../../helpers/admin_service");

const PROD_URL = "https://pasarela-pago-service.ebg.edu.gt";
const SANDBOX_URL = "https://demo-pasarela-pago-service.ebg.edu.gt";

const TEST_CARD = {
  number: "4111111111111111",
  expirationMonth: "12",
  expirationYear: "2031",
  cvv: "123",
};

const codeBoxSx = {
  bgcolor: "#1e1e1e",
  color: "#d4d4d4",
  p: 2,
  borderRadius: 1,
  overflowX: "auto",
  fontFamily: "monospace",
  fontSize: 13,
  whiteSpace: "pre",
};

const CodeBlock = ({ children }) => (
  <Box component="pre" sx={codeBoxSx}>
    {children}
  </Box>
);

const buildJsExample = (baseUrl, email, password) => `const BASE_URL = "${baseUrl}";

async function login(username, password) {
  const res = await fetch(\`\${BASE_URL}/auth/login\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error("Login fallido");
  return res.json(); // { accessToken, idToken, refreshToken, expiresIn }
}

async function refreshAccessToken(refreshToken) {
  const res = await fetch(\`\${BASE_URL}/auth/refresh\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  return res.json(); // { accessToken, idToken, expiresIn }
}

async function authorizePayment(accessToken, payment) {
  const res = await fetch(\`\${BASE_URL}/payments/authorize\`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: \`Bearer \${accessToken}\`,
    },
    body: JSON.stringify(payment),
  });
  return res.json();
}

// --- Uso ---
const { accessToken } = await login("${email}", "${password}");

const resultado = await authorizePayment(accessToken, {
  referenceCode: "ORDEN-0001", // id unico de tu orden, para evitar duplicados
  amount: "150.00",
  currency: "USD",
  capture: false, // false = solo autoriza. true = autoriza y cobra en un paso.
  card: {
    number: "${TEST_CARD.number}",
    expirationMonth: "${TEST_CARD.expirationMonth}",
    expirationYear: "${TEST_CARD.expirationYear}",
    cvv: "${TEST_CARD.cvv}",
  },
  billTo: {
    firstName: "Juan",
    lastName: "Perez",
    address1: "Calle 1",
    locality: "Guatemala",
    administrativeArea: "GU",
    postalCode: "01001",
    country: "GT",
    email: "cliente@correo.com",
    phoneNumber: "50212345678",
  },
});

console.log(resultado.result.data.status); // "AUTHORIZED", "DECLINE", etc.
`;

const buildTsExample = (baseUrl, email, password) => `const BASE_URL = "${baseUrl}";

interface LoginResponse {
  ok: boolean;
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface PaymentRequest {
  referenceCode: string;
  amount: string;
  currency: string;
  capture: boolean;
  card: {
    number: string;
    expirationMonth: string;
    expirationYear: string;
    cvv: string;
  };
  billTo: {
    firstName: string;
    lastName: string;
    address1: string;
    locality: string;
    administrativeArea: string;
    postalCode: string;
    country: string;
    email: string;
    phoneNumber: string;
  };
}

async function login(username: string, password: string): Promise<LoginResponse> {
  const res = await fetch(\`\${BASE_URL}/auth/login\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) throw new Error("Login fallido");
  return res.json();
}

async function refreshAccessToken(refreshToken: string): Promise<LoginResponse> {
  const res = await fetch(\`\${BASE_URL}/auth/refresh\`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  return res.json();
}

async function authorizePayment(accessToken: string, payment: PaymentRequest) {
  const res = await fetch(\`\${BASE_URL}/payments/authorize\`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: \`Bearer \${accessToken}\`,
    },
    body: JSON.stringify(payment),
  });
  return res.json();
}

// --- Uso ---
const { accessToken } = await login("${email}", "${password}");

const resultado = await authorizePayment(accessToken, {
  referenceCode: "ORDEN-0001",
  amount: "150.00",
  currency: "USD",
  capture: false,
  card: {
    number: "${TEST_CARD.number}",
    expirationMonth: "${TEST_CARD.expirationMonth}",
    expirationYear: "${TEST_CARD.expirationYear}",
    cvv: "${TEST_CARD.cvv}",
  },
  billTo: {
    firstName: "Juan",
    lastName: "Perez",
    address1: "Calle 1",
    locality: "Guatemala",
    administrativeArea: "GU",
    postalCode: "01001",
    country: "GT",
    email: "cliente@correo.com",
    phoneNumber: "50212345678",
  },
});

console.log(resultado.result.data.status);
`;

const buildPhpExample = (baseUrl, email, password) => `<?php
$baseUrl = "${baseUrl}";

function postJson(string $url, array $body, ?string $bearerToken = null): array {
    $headers = ["Content-Type: application/json"];
    if ($bearerToken) {
        $headers[] = "Authorization: Bearer $bearerToken";
    }
    $ch = curl_init($url);
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_POSTFIELDS => json_encode($body),
        CURLOPT_HTTPHEADER => $headers,
        CURLOPT_RETURNTRANSFER => true,
    ]);
    $response = curl_exec($ch);
    curl_close($ch);
    return json_decode($response, true);
}

// 1. Iniciar sesion
$login = postJson("$baseUrl/auth/login", [
    "username" => "${email}",
    "password" => "${password}",
]);
$accessToken = $login["accessToken"];

// 2. Autorizar el pago
$pago = postJson("$baseUrl/payments/authorize", [
    "referenceCode" => "ORDEN-0001",
    "amount" => "150.00",
    "currency" => "USD",
    "capture" => false, // false = solo autoriza. true = autoriza y cobra en un paso.
    "card" => [
        "number" => "${TEST_CARD.number}",
        "expirationMonth" => "${TEST_CARD.expirationMonth}",
        "expirationYear" => "${TEST_CARD.expirationYear}",
        "cvv" => "${TEST_CARD.cvv}",
    ],
    "billTo" => [
        "firstName" => "Juan",
        "lastName" => "Perez",
        "address1" => "Calle 1",
        "locality" => "Guatemala",
        "administrativeArea" => "GU",
        "postalCode" => "01001",
        "country" => "GT",
        "email" => "cliente@correo.com",
        "phoneNumber" => "50212345678",
    ],
], $accessToken);

echo $pago["result"]["data"]["status"]; // "AUTHORIZED", "DECLINE", etc.
`;

const buildMarkdown = (label, email, password) => `# Integracion con la Pasarela de Pago EBG/ABG

Credenciales generadas para: **${label}**

- **Usuario:** \`${email}\`
- **Contrasena:** \`${password}\`

> **Importante:** estas credenciales representan tu integracion completa (no a un
> usuario final individual). Guardalas de forma segura y **nunca las uses desde
> codigo que corra en el navegador del cliente final** (JavaScript visible en una
> pagina publica, apps sin backend, etc.) — cualquiera que inspeccione el trafico o
> el codigo fuente podria robarlas y hacer pagos usando tu identidad. Usalas siempre
> desde tu backend/servidor.

## Ambientes disponibles

Las mismas credenciales (usuario y contrasena) funcionan en los dos ambientes —
lo unico que cambia es la URL base:

| Ambiente | URL base | Para que sirve |
|---|---|---|
| **Sandbox (pruebas)** | \`${SANDBOX_URL}\` | Probar tu integracion sin mover dinero real. Usa la tarjeta de prueba de abajo. |
| **Produccion** | \`${PROD_URL}\` | Cobros reales. Usa datos reales de la tarjeta del cliente, no la tarjeta de prueba. |

Prueba primero en sandbox y, cuando todo funcione, cambia la URL base a la de
produccion en tu backend — el resto del codigo no cambia.

## Tarjeta de prueba (Visa) — solo para sandbox

| Campo | Valor |
|---|---|
| Numero | \`${TEST_CARD.number}\` |
| Mes de expiracion | \`${TEST_CARD.expirationMonth}\` |
| Ano de expiracion | \`${TEST_CARD.expirationYear}\` |
| CVV | \`${TEST_CARD.cvv}\` |

Esta tarjeta solo funciona contra \`${SANDBOX_URL}\`. Contra produccion no es valida.

## Como funciona

1. Tu backend inicia sesion una vez con usuario y contrasena y recibe un token.
2. Tu backend usa ese token para autorizar pagos.
3. Cuando el token expira (60 minutos), tu backend lo renueva con el refresh token,
   sin volver a enviar usuario/contrasena. El refresh token dura 30 dias; despues de
   eso hay que iniciar sesion de nuevo con usuario/contrasena.

## 1. Iniciar sesion

\`POST <URL base>/auth/login\`

Body:
\`\`\`json
{ "username": "${email}", "password": "${password}" }
\`\`\`

Respuesta:
\`\`\`json
{ "ok": true, "accessToken": "...", "idToken": "...", "refreshToken": "...", "expiresIn": 3600 }
\`\`\`

## 2. Autorizar un pago

\`POST <URL base>/payments/authorize\`
Header: \`Authorization: Bearer <accessToken>\`

Body (ejemplo con la tarjeta de prueba, para sandbox):
\`\`\`json
{
  "referenceCode": "id unico de tu orden (para evitar duplicados)",
  "amount": "150.00",
  "currency": "USD",
  "capture": false,
  "card": { "number": "${TEST_CARD.number}", "expirationMonth": "${TEST_CARD.expirationMonth}", "expirationYear": "${TEST_CARD.expirationYear}", "cvv": "${TEST_CARD.cvv}" },
  "billTo": {
    "firstName": "Juan", "lastName": "Perez", "address1": "Calle 1",
    "locality": "Guatemala", "administrativeArea": "GU", "postalCode": "01001",
    "country": "GT", "email": "cliente@correo.com", "phoneNumber": "50212345678"
  }
}
\`\`\`

- \`capture: false\` = solo autoriza (no cobra todavia, sirve para reservar fondos).
  \`capture: true\` = autoriza y cobra en un solo paso.
- La respuesta trae \`result.data.status\`: \`AUTHORIZED\` (aprobado), \`DECLINE\`
  (rechazado), \`AUTHORIZED_RISK_DECLINED\` (rechazado por el motor antifraude), etc.

## 3. Renovar el token

\`POST <URL base>/auth/refresh\`

Body:
\`\`\`json
{ "refreshToken": "..." }
\`\`\`

## Ejemplos de codigo (sandbox, con la tarjeta de prueba)

Los ejemplos usan la URL de **sandbox** (\`${SANDBOX_URL}\`) y la tarjeta de prueba de
arriba, para que puedas probarlos tal cual. Cuando pases a produccion, cambia
\`BASE_URL\`/\`$baseUrl\` a \`${PROD_URL}\` y reemplaza los datos de la tarjeta de prueba
por los datos reales de la tarjeta del cliente.

### JavaScript

\`\`\`javascript
${buildJsExample(SANDBOX_URL, email, password)}
\`\`\`

### TypeScript

\`\`\`typescript
${buildTsExample(SANDBOX_URL, email, password)}
\`\`\`

### PHP

\`\`\`php
${buildPhpExample(SANDBOX_URL, email, password)}
\`\`\`

## Notas de seguridad (PCI-DSS)

- Nunca guardes ni registres en logs el numero completo de tarjeta ni el CVV.
- Usa siempre HTTPS de extremo a extremo.
- Trata el usuario y la contrasena de esta integracion como cualquier credencial de
  produccion: no los subas a un repositorio de codigo, no los compartas por canales
  inseguros.
`;

const CreatePasarelaPagoIntegracion = () => {
  const [contador] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formConfig, setFormConfig] = useState(null);
  const [credenciales, setCredenciales] = useState(null);

  useEffect(() => {
    setFormConfig({
      title: `Generar credenciales de integracion`,
      submitTitle: "Generar",
      formConfig: [
        {
          type: 1,
          required: true,
          value: "",
          name: "label",
          label: "Ej. Portal propio EBG, o nombre del socio externo",
          title: "Nombre de la integracion",
        },
        {
          type: 4,
          required: true,
          value: "",
          name: "email",
          label: "Identificador unico (formato email)",
          title: "Usuario",
        },
      ],
    });
    setLoading(false);
  }, [contador]);

  const afterSubmit = (body) => {
    return new Promise((resolve, reject) => {
      const jwt = JSON.parse(Cookies.get(process.env.REACT_APP_COOKIE_KEY));
      admin_service
        .postDataAuth(
          "/pasarela-pago-integraciones",
          jwt.TokenType,
          jwt.accessToken,
          body
        )
        .then((response_database) => {
          setCredenciales(response_database.data.response);
          resolve();
        })
        .catch((error) => {
          console.log(error);
          reject(
            "Ocurrio un error al generar las credenciales, intente de nuevo"
          );
        });
    });
  };

  const downloadInstructions = () => {
    const markdown = buildMarkdown(
      credenciales.label,
      credenciales.email,
      credenciales.password
    );
    const blob = new Blob([markdown], { type: "text/markdown" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `integracion-pasarela-pago-${credenciales.email}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ width: "100%" }} paddingTop={5} paddingBottom={5}>
        <Stack spacing={5}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href={"/pasarela-pago-integraciones"}
            >
              Integraciones Pasarela de Pago
            </Link>
            <Typography color="text.primary">Generar credenciales</Typography>
          </Breadcrumbs>
          {!credenciales && (
            <Formulario afterSubmit={afterSubmit} formConfig={formConfig} />
          )}
          {credenciales && (
            <Paper sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Alert severity="warning">
                  Copia estas credenciales o descarga las instrucciones ahora:
                  la contraseña no se puede volver a mostrar.
                </Alert>
                <Typography>
                  <b>Usuario:</b> {credenciales.email}
                </Typography>
                <Typography>
                  <b>Contraseña:</b> {credenciales.password}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={downloadInstructions}
                  sx={{ width: { xs: "100%", md: "100%", lg: "35%" } }}
                >
                  Descargar instrucciones de integracion (.md)
                </Button>

                <Divider />

                <Typography variant="h6">
                  Instrucciones para integrar esta pasarela de pago
                </Typography>
                <Typography>
                  1. Tu backend inicia sesion una vez con usuario y contraseña
                  contra <code>POST &lt;URL base&gt;/auth/login</code> y recibe
                  un token.
                </Typography>
                <Typography>
                  2. Tu backend usa ese token (
                  <code>Authorization: Bearer &lt;accessToken&gt;</code>) para
                  autorizar pagos contra{" "}
                  <code>POST &lt;URL base&gt;/payments/authorize</code>.
                </Typography>
                <Typography>
                  3. Cuando el token expira (60 minutos), tu backend lo renueva
                  con <code>POST &lt;URL base&gt;/auth/refresh</code>, sin
                  volver a enviar usuario/contraseña.
                </Typography>
                <Alert severity="info">
                  Nunca uses este usuario y contraseña desde JavaScript que
                  corra en el navegador del cliente final — solo desde tu
                  backend/servidor.
                </Alert>

                <Divider />

                <Typography variant="h6">Ambientes disponibles</Typography>
                <Typography>
                  Las mismas credenciales funcionan en los dos ambientes, solo
                  cambia la URL base:
                </Typography>
                <Typography>
                  <b>Sandbox (pruebas):</b> <code>{SANDBOX_URL}</code> — no
                  mueve dinero real, usa la tarjeta de prueba de abajo.
                </Typography>
                <Typography>
                  <b>Producción:</b> <code>{PROD_URL}</code> — cobros reales,
                  usa los datos reales de la tarjeta del cliente.
                </Typography>

                <Alert severity="warning">
                  <b>Tarjeta de prueba (Visa) — solo funciona en sandbox:</b>
                  <br />
                  Número: {TEST_CARD.number} — Expiración:{" "}
                  {TEST_CARD.expirationMonth}/{TEST_CARD.expirationYear} —
                  CVV: {TEST_CARD.cvv}
                </Alert>

                <Typography variant="subtitle2">
                  Ejemplos de código (sandbox, con la tarjeta de prueba) —
                  antes de ir a producción, cambia la URL base y usa datos
                  reales de tarjeta.
                </Typography>

                <Typography variant="subtitle1">JavaScript</Typography>
                <CodeBlock>
                  {buildJsExample(
                    SANDBOX_URL,
                    credenciales.email,
                    credenciales.password
                  )}
                </CodeBlock>

                <Typography variant="subtitle1">TypeScript</Typography>
                <CodeBlock>
                  {buildTsExample(
                    SANDBOX_URL,
                    credenciales.email,
                    credenciales.password
                  )}
                </CodeBlock>

                <Typography variant="subtitle1">PHP</Typography>
                <CodeBlock>
                  {buildPhpExample(
                    SANDBOX_URL,
                    credenciales.email,
                    credenciales.password
                  )}
                </CodeBlock>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
    </React.Fragment>
  );
};

export default CreatePasarelaPagoIntegracion;
